import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CallRecordsService } from '../../../core/services/call-records/call-records.service';
import { CallResource } from '../../../core/interfaces/communication.interface';
import { CallDetailComponent } from '../../voip/call-detail/call-detail.component';

type CallPanelState = 'idle' | 'dialing' | 'ringing' | 'in-call' | 'ended' | 'error';

@Component({
  selector: 'app-call-panel',
  standalone: true,
  imports: [CommonModule, TranslateModule, CallDetailComponent],
  templateUrl: './call-panel.component.html',
  styleUrl: './call-panel.component.css',
})
export class CallPanelComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() phoneNumber = '';
  @Input() contactName = '';
  @Input() clientReference = '';
  @Output() closed = new EventEmitter<void>();

  // Call state
  callState: CallPanelState = 'idle';
  callDuration = 0;

  // Call detail
  selectedCall: CallResource | null = null;
  selectedCallLoading = false;
  private postCallPolling: any = null;

  // Call history
  clientCalls: CallResource[] = [];
  clientCallsTotal = 0;
  clientCallsPage = 1;
  clientCallsPageSize = 5;
  clientCallsLoading = false;
  clientCallsError = false;
  clientCallsLoaded = false;

  constructor(private callRecordsService: CallRecordsService) {}

  @HostListener('window:softphone-state-change', ['$event'])
  onSoftphoneStateChange(event: CustomEvent): void {
    const { state, duration } = event.detail;
    this.callState = state as CallPanelState;
    this.callDuration = duration ?? 0;

    if (state === 'ended' && this.clientReference) {
      this.loadLatestCallDetail();
    }
  }

  private loadLatestCallDetail(): void {
    setTimeout(() => {
      this.callRecordsService.getCallsByClient(this.clientReference, { page: 1, pageSize: 1 }).subscribe({
        next: (res: any) => {
          const calls = res.data || [];
          if (calls.length > 0) {
            this.selectedCall = calls[0];
            this.startPostCallPolling(calls[0].reference);
          }
          this.resetClientCalls();
          this.loadClientCalls();
        },
        error: () => {
          this.resetClientCalls();
          this.loadClientCalls();
        },
      });
    }, 3000);
  }

  private startPostCallPolling(reference: string): void {
    this.stopPostCallPolling();
    let attempts = 0;
    this.postCallPolling = setInterval(() => {
      attempts++;
      this.callRecordsService.getCall(reference).subscribe({
        next: (res: any) => {
          if (res.data) {
            this.selectedCall = res.data;
            // Stop when recording and transcript are ready
            const rec = res.data.has_recording && res.data.recording?.status === 'stored';
            const trans = !res.data.transcript_status || res.data.transcript_status === 'completed' || res.data.transcript_status === 'failed' || res.data.transcript_status === 'skipped';
            if ((rec && trans) || attempts >= 18) {
              this.stopPostCallPolling();
            }
          }
        },
      });
    }, 10000);
  }

  private stopPostCallPolling(): void {
    if (this.postCallPolling) {
      clearInterval(this.postCallPolling);
      this.postCallPolling = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientReference'] || changes['visible']) {
      if (this.visible && this.clientReference) {
        this.resetClientCalls();
        this.loadClientCalls();
      }
      if (this.visible) {
        this.callState = 'idle';
        this.callDuration = 0;
      }
    }
  }

  ngOnDestroy(): void {
    this.callState = 'idle';
    this.stopPostCallPolling();
  }

  get contactInitial(): string {
    return (this.contactName || this.phoneNumber || '?').charAt(0).toUpperCase();
  }

  get isCallActive(): boolean {
    return this.callState !== 'idle' && this.callState !== 'ended' && this.callState !== 'error';
  }

  initiateCall(): void {
    if (!this.phoneNumber) return;
    this.callState = 'dialing';
    const cleanPhone = this.phoneNumber.replace(/[() \-]/g, '');
    window.dispatchEvent(new CustomEvent('softphone-call', {
      detail: { phone: cleanPhone, name: this.contactName, client_reference: this.clientReference, silent: true },
    }));
  }

  hangupCall(): void {
    window.dispatchEvent(new CustomEvent('softphone-hangup'));
  }

  close(): void {
    this.stopPostCallPolling();
    this.closed.emit();
  }

  formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  getStateLabel(): string {
    const map: Record<string, string> = {
      idle: '',
      dialing: 'communication.voip.dialing',
      ringing: 'communication.voip.ringing',
      'in-call': 'communication.voip.in_call',
      ended: 'communication.voip.call_ended',
      error: 'communication.voip.call_failed',
    };
    return map[this.callState] || '';
  }

  getStateColor(): string {
    const map: Record<string, string> = {
      idle: '', dialing: '#f1b44c', ringing: '#f1b44c',
      'in-call': '#dc3545', ended: '#22c55e', error: '#dc3545',
    };
    return map[this.callState] || '';
  }

  // === Call Detail ===

  openCallDetail(call: CallResource): void {
    this.selectedCallLoading = true;
    this.callRecordsService.getCall(call.reference).subscribe({
      next: (res: any) => {
        this.selectedCall = res.data || call;
        this.selectedCallLoading = false;
      },
      error: () => {
        this.selectedCall = call;
        this.selectedCallLoading = false;
      },
    });
  }

  closeCallDetail(): void {
    this.selectedCall = null;
    this.stopPostCallPolling();
  }

  // === Client Call History ===

  loadClientCalls(): void {
    if (!this.clientReference) return;

    this.clientCallsLoading = true;
    this.clientCallsError = false;

    this.callRecordsService.getCallsByClient(this.clientReference, {
      page: this.clientCallsPage,
      pageSize: this.clientCallsPageSize,
    }).subscribe({
      next: (res: any) => {
        const newCalls = res.data || [];
        if (this.clientCallsPage === 1) {
          this.clientCalls = newCalls;
        } else {
          this.clientCalls = [...this.clientCalls, ...newCalls];
        }
        this.clientCallsTotal = res.meta?.total ?? res.total ?? newCalls.length;
        this.clientCallsLoading = false;
        this.clientCallsLoaded = true;
      },
      error: () => {
        this.clientCallsLoading = false;
        this.clientCallsError = true;
      },
    });
  }

  loadMoreClientCalls(): void {
    this.clientCallsPage++;
    this.loadClientCalls();
  }

  get hasMoreClientCalls(): boolean {
    return this.clientCalls.length < this.clientCallsTotal;
  }

  resetClientCalls(): void {
    this.clientCalls = [];
    this.clientCallsTotal = 0;
    this.clientCallsPage = 1;
    this.clientCallsLoading = false;
    this.clientCallsError = false;
    this.clientCallsLoaded = false;
  }

  formatCallDuration(seconds: number | null): string {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
